import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from './ui/use-toast';
import { Input } from './ui/input';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const FundDealModal = ({ isOpen, onClose, deal, onFund }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    funding_amount: '',
    payback_amount: '',
    factor_rate: '',
    payment_frequency: 'daily',
    term_length: '',
    payment_amount: '',
    first_payment_date: '',
    funding_date: new Date().toISOString().split('T')[0],
    broker_commission_percent: '0',
    origination_fee_percent: '0',
    notes: ''
  });

  const [calculations, setCalculations] = useState({
    netFunded: 0,
    originationFeeAmount: 0,
    brokerCommissionAmount: 0,
    grossProfit: 0,
    netProfit: 0
  });
  const [loading, setLoading] = useState(false);

  // Pre-fill from deal data when modal opens
  useEffect(() => {
    if (deal && isOpen) {
      const fundingAmt = deal.funding_amount || deal.advanced || 0;
      const paybackAmt = deal.payback_amount || deal.payback || 0;
      const initialFactor = (fundingAmt > 0 && paybackAmt > 0) ? (paybackAmt / fundingAmt).toFixed(4) : '';
      setFormData({
        funding_amount: fundingAmt ? fundingAmt.toString() : '',
        payback_amount: paybackAmt ? paybackAmt.toString() : '',
        factor_rate: initialFactor,
        payment_frequency: deal.payment_frequency || 'daily',
        term_length: deal.term_length?.toString() || '',
        payment_amount: deal.payment_amount?.toString() || '',
        first_payment_date: deal.first_payment_date || '',
        funding_date: new Date().toISOString().split('T')[0],
        broker_commission_percent: '0',
        origination_fee_percent: '0',
        notes: deal.notes || ''
      });
    }
  }, [deal, isOpen]);

  // Calculate amounts when values change
  useEffect(() => {
    const fundingAmt = parseFloat(formData.funding_amount) || 0;
    const paybackAmt = parseFloat(formData.payback_amount) || 0;
    const origFeePct = parseFloat(formData.origination_fee_percent) || 0;
    const brokerCommPct = parseFloat(formData.broker_commission_percent) || 0;
    const dailyPayment = parseFloat(formData.payment_amount) || 0;
    
    // Origination fee comes OUT of funded amount — % of funded
    const origFeeAmount = fundingAmt * (origFeePct / 100);
    // Net wired to merchant = funded − origination fee. We never wire the fee, we keep it.
    const netFundedToMerchant = fundingAmt - origFeeAmount;
    
    // Broker commission is % of FUNDED amount (industry convention), NOT % of profit.
    // e.g., $100k funded × 10% = $10k broker commission.
    const brokerCommAmount = fundingAmt * (brokerCommPct / 100);
    
    // GROSS PROFIT = payback − net wired (not payback − funded), because we kept the
    // origination fee — it was never wired out. Example: $150k payback, $90k net wired
    // → $60k gross profit (the fee implicitly adds back to profit).
    const grossProfit = paybackAmt - netFundedToMerchant;
    
    // NET PROFIT = gross profit − broker commission (a real cash outflow)
    const netProfit = grossProfit - brokerCommAmount;
    
    // Rule A: Term from daily payment
    const termDays = dailyPayment > 0 ? Math.round(paybackAmt / dailyPayment) : 0;
    const termWeeks = termDays > 0 ? (termDays / 5).toFixed(1) : '0';
    
    // Rule B: Factor rate from funded and payback (display only — input drives state)
    const factorRate = fundingAmt > 0 ? (paybackAmt / fundingAmt).toFixed(4) : '0.0000';
    
    // Rule C: Fee % from NET wired
    const feePct = fundingAmt > 0 ? (((fundingAmt - netFundedToMerchant) / fundingAmt) * 100).toFixed(2) : '0.00';
    
    setCalculations({
      netFunded: netFundedToMerchant,
      originationFeeAmount: origFeeAmount,
      brokerCommissionAmount: brokerCommAmount,
      grossProfit,
      netProfit,
      termDays,
      termWeeks,
      factorRate,
      feePct,
    });
  }, [formData.funding_amount, formData.payback_amount, formData.origination_fee_percent, formData.broker_commission_percent, formData.payment_amount]);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      const funding = parseFloat(next.funding_amount) || 0;
      const factor = parseFloat(next.factor_rate) || 0;
      const term = parseInt(next.term_length) || 0;
      
      // Auto-calc 1 — Payback = Funding × Factor Rate
      // Triggered ONLY by changes to funding_amount or factor_rate (one-way: factor → payback).
      if ((field === 'funding_amount' || field === 'factor_rate') && funding > 0 && factor > 0) {
        next.payback_amount = (funding * factor).toFixed(2);
      }
      
      // Auto-calc 2 — Daily/Weekly Payment = Payback ÷ Term
      // Triggered when any input that changes payback or term length changes.
      const payback = parseFloat(next.payback_amount) || 0;
      if (
        (field === 'funding_amount' || field === 'factor_rate' || field === 'term_length' || field === 'payback_amount')
        && payback > 0 && term > 0
      ) {
        next.payment_amount = (payback / term).toFixed(2);
      }
      
      return next;
    });
  };

  const handleSubmit = async () => {
    // Validate required fields using toast (alert() is blocked in some async contexts)
    if (!formData.funding_amount || parseFloat(formData.funding_amount) <= 0) {
      toast({ title: 'Missing field', description: 'Funding Amount is required', variant: 'destructive' });
      return;
    }
    if (!formData.payback_amount || parseFloat(formData.payback_amount) <= 0) {
      toast({ title: 'Missing field', description: 'Payback Amount is required', variant: 'destructive' });
      return;
    }
    if (!formData.factor_rate || parseFloat(formData.factor_rate) <= 0) {
      toast({ title: 'Missing field', description: 'Factor Rate is required', variant: 'destructive' });
      return;
    }
    if (!formData.term_length || parseInt(formData.term_length) <= 0) {
      toast({ title: 'Missing field', description: 'Term Length (Days) is required', variant: 'destructive' });
      return;
    }
    if (!formData.first_payment_date) {
      toast({ title: 'Missing field', description: 'First Payment Date is required', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      await onFund({
        funding_amount: formData.funding_amount,
        payback_amount: formData.payback_amount,
        payment_frequency: formData.payment_frequency,
        term_length: formData.term_length,
        payment_amount: formData.payment_amount,
        first_payment_date: formData.first_payment_date,
        funding_date: formData.funding_date,
        broker_commission: (calculations.brokerCommissionAmount || 0).toString(),
        origination_fee: (calculations.originationFeeAmount || 0).toString(),
        net_funded: calculations.netFunded || 0,
        notes: formData.notes || ''
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Fund Deal - Final Terms</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <div className="bg-purple-50 p-4 rounded-lg mb-6">
          <p className="text-sm text-purple-800">
            <strong>Business:</strong> {deal?.business || deal?.business_name}
          </p>
          <p className="text-sm text-purple-800 mt-1">
            Review and adjust terms before funding. This will move the deal to Funded status.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Funding Amount */}
          <div>
            <label className="block text-sm font-medium mb-1">Funding Amount *</label>
            <input
              type="number"
              data-testid="funding-amount-input"
              value={formData.funding_amount}
              onChange={(e) => handleChange('funding_amount', e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* Payback Amount */}
          <div>
            <label className="block text-sm font-medium mb-1">Payback Amount *</label>
            <input
              type="number"
              data-testid="payback-amount-input"
              value={formData.payback_amount}
              onChange={(e) => handleChange('payback_amount', e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* Factor Rate (NEW required input — auto-calcs payback) */}
          <div>
            <label className="block text-sm font-medium mb-1">Factor Rate *</label>
            <input
              type="number"
              step="0.01"
              data-testid="factor-rate-input"
              value={formData.factor_rate}
              onChange={(e) => handleChange('factor_rate', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="1.45"
            />
            <p className="text-xs text-gray-500 mt-1">Funding × Factor Rate = Payback</p>
          </div>

          {/* Broker Commission % */}
          <div>
            <label className="block text-sm font-medium mb-1">ISO/Broker Commission %</label>
            <Input
              type="number"
              step="0.1"
              data-testid="broker-commission-input"
              value={formData.broker_commission_percent}
              onChange={(e) => handleChange('broker_commission_percent', e.target.value)}
              className="w-full"
              placeholder="20"
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.broker_commission_percent || 0}% of funded = ${calculations.brokerCommissionAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          </div>

          {/* Origination Fee % */}
          <div>
            <label className="block text-sm font-medium mb-1">Origination Fee %</label>
            <Input
              type="number"
              step="0.1"
              value={formData.origination_fee_percent}
              onChange={(e) => handleChange('origination_fee_percent', e.target.value)}
              className="w-full"
              placeholder="10"
            />
            <p className="text-xs text-gray-500 mt-1">
              Deducted from funded: ${calculations.originationFeeAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          </div>

          {/* Auto-Calculations Display */}
          <div className="col-span-2 bg-green-50 p-4 rounded space-y-2">
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              <div className="flex justify-between">
                <span className="text-sm font-medium">Net Funded to Merchant:</span>
                <span className="text-lg font-bold text-green-700">
                  ${calculations.netFunded.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Factor Rate:</span>
                <span className="text-lg font-bold text-blue-700">{calculations.factorRate}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Gross Profit:</span>
                <span className="font-semibold">${calculations.grossProfit.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Fee %:</span>
                <span className="font-semibold">{calculations.feePct}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Net Profit (after commission):</span>
                <span className="font-semibold text-green-600">${calculations.netProfit.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Calc Term (business days):</span>
                <span className="font-semibold">{calculations.termDays} days ({calculations.termWeeks} weeks)</span>
              </div>
            </div>
          </div>

          {/* Payment Frequency */}
          <div>
            <label className="block text-sm font-medium mb-1">Payment Frequency *</label>
            <select
              value={formData.payment_frequency}
              onChange={(e) => handleChange('payment_frequency', e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>

          {/* Term Length */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Term Length ({formData.payment_frequency === 'daily' ? 'Days' : 'Weeks'}) *
            </label>
            <input
              type="number"
              data-testid="term-days-input"
              value={formData.term_length}
              onChange={(e) => handleChange('term_length', e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* Payment Amount */}
          <div>
            <label className="block text-sm font-medium mb-1">Payment Amount *</label>
            <input
              type="number"
              step="0.01"
              value={formData.payment_amount}
              onChange={(e) => handleChange('payment_amount', e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* First Payment Date */}
          <div>
            <label className="block text-sm font-medium mb-1">First Payment Date *</label>
            <input
              type="date"
              value={formData.first_payment_date}
              onChange={(e) => handleChange('first_payment_date', e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* Funding Date */}
          <div>
            <label className="block text-sm font-medium mb-1">Funding Date *</label>
            <input
              type="date"
              value={formData.funding_date}
              onChange={(e) => handleChange('funding_date', e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          {/* Notes */}
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              className="w-full border rounded px-3 py-2"
              rows="3"
              placeholder="Funding notes"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 bg-green-600 hover:bg-green-700"
            data-testid="fund-deal-submit-btn"
            type="submit"
          >
            {loading ? 'Funding...' : 'Fund Deal'}
          </Button>
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            disabled={loading}
            className="flex-1"
          >
            Cancel
          </Button>
        </div>
        </form>
      </div>
    </div>
  );
};

export default FundDealModal;
